import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Wallet, Users, Clock, Receipt,
  Plus, X, Check, RefreshCw, IndianRupee, Repeat, UserPlus, Trash2,
  Package, AlertTriangle, ShoppingCart, Pencil,
} from 'lucide-react';

const API = '/api';

/* ── helpers ─────────────────────────────────────────────── */

const inr = (n) => {
  const v = Number(n) || 0;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${Math.round(v)}`;
};

const inrFull = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

// IST date — salon owners think in India time, not UTC
const istToday = () =>
  new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().split('T')[0];

const PAYMENT_MODES = [
  { id: 'cash', label: '💵 Cash' },
  { id: 'upi', label: '📱 UPI' },
  { id: 'card', label: '💳 Card' },
  { id: 'other', label: '📝 Other' },
];

const EXPENSE_CATEGORIES = [
  { id: 'supplies', label: '🧴 Supplies' },
  { id: 'salary', label: '👥 Staff Salary' },
  { id: 'rent', label: '🏠 Rent' },
  { id: 'utilities', label: '💡 Utilities' },
  { id: 'marketing', label: '📢 Marketing' },
  { id: 'other', label: '📦 Other' },
];

/* ── small UI pieces ─────────────────────────────────────── */

function StatCard({ label, value, sub, icon, accent, delay = 0 }) {
  return (
    <div
      className="animate-fade-up"
      style={{
        background: 'white',
        borderRadius: '18px',
        padding: '16px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        animationDelay: `${delay}s`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '3px', height: '100%',
        background: accent,
      }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
          </p>
          <p style={{
            fontSize: '1.45rem', fontWeight: 800, color: '#0f172a',
            fontFamily: "'Syne',sans-serif", marginTop: '4px', lineHeight: 1.1,
          }}>
            {value}
          </p>
          {sub && <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '3px' }}>{sub}</p>}
        </div>
        <div style={{
          width: '34px', height: '34px', borderRadius: '10px',
          background: `${accent}18`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: accent, flexShrink: 0,
        }}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function TrendChart({ data }) {
  const max = Math.max(...data.map((d) => d.revenue), 1);
  const todayStr = istToday();

  return (
    <div style={{
      background: 'white', borderRadius: '18px', padding: '18px',
      border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    }}>
      <p style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', fontFamily: "'Syne',sans-serif", marginBottom: '2px' }}>
        Last 7 Days
      </p>
      <p style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '16px' }}>
        Daily revenue trend
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '110px' }}>
        {data.map((d, i) => {
          const pct = max > 0 ? (d.revenue / max) * 100 : 0;
          const isToday = d.date === todayStr;
          return (
            <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              <span style={{
                fontSize: '0.58rem', fontWeight: 700,
                color: d.revenue > 0 ? '#475569' : '#cbd5e1',
              }}>
                {d.revenue > 0 ? inr(d.revenue) : '–'}
              </span>
              <div style={{
                width: '100%',
                height: `${Math.max(pct, 3)}%`,
                minHeight: '4px',
                borderRadius: '6px 6px 3px 3px',
                background: isToday
                  ? 'linear-gradient(180deg,#dc2626,#e11d48)'
                  : d.revenue > 0
                    ? 'linear-gradient(180deg,#c4b5fd,#a78bfa)'
                    : '#f1f5f9',
                transition: 'height 0.5s cubic-bezier(0.16,1,0.3,1)',
                animation: `growBar 0.6s ease ${i * 0.06}s both`,
              }} />
              <span style={{
                fontSize: '0.6rem', fontWeight: isToday ? 800 : 600,
                color: isToday ? '#dc2626' : '#94a3b8',
              }}>
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes growBar { from { height: 0 } }`}</style>
    </div>
  );
}

/* ── main component ──────────────────────────────────────── */

export default function SalonManager({ salonId, getAuthHeaders, onDataChange }) {
  const inFlight = useRef(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('overview');

  // dialogs
  const [settleFor, setSettleFor] = useState(null);
  const [settleAmount, setSettleAmount] = useState('');
  const [settleMode, setSettleMode] = useState('cash');
  const [saving, setSaving] = useState(false);

  const [showWalkIn, setShowWalkIn] = useState(false);
  const [walkIn, setWalkIn] = useState({ service_name: '', amount: '', customer_name: '', payment_mode: 'cash' });

  const [showExpense, setShowExpense] = useState(false);
  const [expense, setExpense] = useState({ title: '', amount: '', category: 'supplies' });

  // products
  const [products, setProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [productForm, setProductForm] = useState({
    product_id: null, name: '', price: '', cost_price: '', stock: '', low_stock_alert: '3', unit: 'pcs',
  });
  const [sellFor, setSellFor] = useState(null);
  const [sellQty, setSellQty] = useState('1');
  const [sellMode, setSellMode] = useState('cash');

  const load = useCallback(async (silent = false) => {
    if (!salonId) return;
    // Guard against overlapping requests. If a dependency identity ever
    // becomes unstable again, this stops it turning into a request storm.
    if (inFlight.current) return;
    inFlight.current = true;
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${API}/salon/${salonId}/manager/summary`, {
        headers, timeout: 20000,
      });
      setData(res.data);
    } catch (e) {
      console.error('Manager load failed:', e);
      if (!silent) {
        toast.error('Could not load data', {
          description: e?.response?.data?.error || 'Please check your connection.',
        });
      }
    } finally {
      inFlight.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [salonId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const loadProducts = useCallback(async () => {
    if (!salonId) return;
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${API}/salon/${salonId}/manager/products`, {
        headers, timeout: 20000,
      });
      setProducts(res.data.products || []);
      setProductsLoaded(true);
    } catch (e) {
      console.error('Products load failed:', e);
      setProductsLoaded(true);
    }
  }, [salonId, getAuthHeaders]);

  // Lazy-load the catalogue only when the owner opens the Products tab.
  // NOTE: must come AFTER loadProducts is declared — const is not hoisted,
  // so referencing it earlier throws "Cannot access before initialization".
  useEffect(() => {
    if (tab === 'products' && !productsLoaded) loadProducts();
  }, [tab, productsLoaded, loadProducts]);

  const submitProduct = async () => {
    const price = Number(productForm.price);
    if (!productForm.name.trim()) { toast.error('Enter product name'); return; }
    if (!Number.isFinite(price) || price <= 0) { toast.error('Enter a valid price'); return; }

    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/salon/${salonId}/manager/product`, {
        product_id: productForm.product_id,
        name: productForm.name,
        price,
        cost_price: Number(productForm.cost_price) || 0,
        stock: Number(productForm.stock) || 0,
        low_stock_alert: Number(productForm.low_stock_alert) || 3,
        unit: productForm.unit,
      }, { headers, timeout: 20000 });

      toast.success(productForm.product_id ? 'Product updated' : 'Product added');
      setShowProductForm(false);
      setProductForm({ product_id: null, name: '', price: '', cost_price: '', stock: '', low_stock_alert: '3', unit: 'pcs' });
      loadProducts();
      load(true);
    } catch (e) {
      toast.error('Could not save', { description: e?.response?.data?.error || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const submitSale = async () => {
    const qty = Number(sellQty);
    if (!Number.isFinite(qty) || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    if (qty > (Number(sellFor.stock) || 0)) {
      toast.error(`Only ${sellFor.stock} in stock`);
      return;
    }

    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.post(`${API}/salon/${salonId}/manager/sale`, {
        product_id: sellFor.product_id,
        quantity: qty,
        payment_mode: sellMode,
      }, { headers, timeout: 20000 });

      toast.success(`Sold — ${inrFull(res.data.total)}`, {
        description: `${res.data.remaining_stock} left in stock`,
      });
      setSellFor(null);
      setSellQty('1');
      loadProducts();
      load(true);
      onDataChange?.();
    } catch (e) {
      toast.error('Could not record sale', { description: e?.response?.data?.error || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/salon/${salonId}/manager/product/${id}`, { headers, timeout: 15000 });
      toast.success('Product removed');
      loadProducts();
    } catch (e) {
      toast.error('Could not delete', { description: e?.response?.data?.error || 'Please try again.' });
    }
  };

  const openSettle = (b) => {
    setSettleFor(b);
    setSettleAmount(String(b.quoted_price || ''));
    setSettleMode('cash');
  };

  const submitSettle = async () => {
    const amt = Number(settleAmount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/salon/${salonId}/manager/settle`, {
        booking_id: settleFor.booking_id,
        final_amount: amt,
        payment_mode: settleMode,
      }, { headers, timeout: 20000 });

      toast.success(`Recorded ${inrFull(amt)}`);
      setSettleFor(null);
      load(true);
      onDataChange?.();
    } catch (e) {
      toast.error('Could not save', { description: e?.response?.data?.error || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const submitWalkIn = async () => {
    const amt = Number(walkIn.amount);
    if (!walkIn.service_name.trim()) { toast.error('Enter service name'); return; }
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }

    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/salon/${salonId}/manager/walkin`, {
        ...walkIn, amount: amt, date: istToday(),
      }, { headers, timeout: 20000 });

      toast.success(`Walk-in recorded — ${inrFull(amt)}`);
      setShowWalkIn(false);
      setWalkIn({ service_name: '', amount: '', customer_name: '', payment_mode: 'cash' });
      load(true);
      onDataChange?.();
    } catch (e) {
      toast.error('Could not save', { description: e?.response?.data?.error || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const submitExpense = async () => {
    const amt = Number(expense.amount);
    if (!expense.title.trim()) { toast.error('Enter a title'); return; }
    if (!Number.isFinite(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }

    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/salon/${salonId}/manager/expense`, {
        ...expense, amount: amt, date: istToday(),
      }, { headers, timeout: 20000 });

      toast.success('Expense added');
      setShowExpense(false);
      setExpense({ title: '', amount: '', category: 'supplies' });
      load(true);
    } catch (e) {
      toast.error('Could not save', { description: e?.response?.data?.error || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API}/salon/${salonId}/manager/expense/${id}`, { headers, timeout: 15000 });
      toast.success('Expense removed');
      load(true);
    } catch (e) {
      toast.error('Could not delete', { description: e?.response?.data?.error || 'Please try again.' });
    }
  };

  /* ── loading skeleton ── */
  if (loading) {
    return (
      <div style={{ display: 'grid', gap: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              height: '92px', borderRadius: '18px',
              background: 'linear-gradient(90deg,#f8fafc 25%,#f1f5f9 50%,#f8fafc 75%)',
              backgroundSize: '200% 100%', animation: 'shimmer 1.3s infinite',
            }} />
          ))}
        </div>
        <div style={{
          height: '180px', borderRadius: '18px',
          background: 'linear-gradient(90deg,#f8fafc 25%,#f1f5f9 50%,#f8fafc 75%)',
          backgroundSize: '200% 100%', animation: 'shimmer 1.3s infinite',
        }} />
        <style>{`@keyframes shimmer { to { background-position: -200% 0 } }`}</style>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 20px', background: 'white',
        borderRadius: '18px', border: '1px solid #f1f5f9',
      }}>
        <p style={{ fontSize: '32px', marginBottom: '10px' }}>📊</p>
        <p style={{ fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>Could not load your data</p>
        <button onClick={() => load()} style={{
          background: 'linear-gradient(135deg,#dc2626,#e11d48)', color: 'white',
          border: 'none', borderRadius: '12px', padding: '10px 20px',
          fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', marginTop: '8px',
        }}>
          Try Again
        </button>
      </div>
    );
  }

  const { today, week, month, growth, dailyTrend, topServices, topProducts,
          lowStock, paymentSplit, pendingSettlement, expenses, expenseTotal,
          netProfit, insights } = data;

  const maxSvc = Math.max(...(topServices || []).map((s) => s.revenue), 1);
  const paySum = Object.values(paymentSplit || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div style={{ display: 'grid', gap: '14px' }}>

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: '1.15rem', color: '#0f172a' }}>
            Salon Manager
          </h2>
          <p style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            Your business at a glance
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          style={{
            width: '34px', height: '34px', borderRadius: '10px',
            border: '1px solid #e2e8f0', background: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#64748b',
          }}
          aria-label="Refresh"
        >
          <RefreshCw size={15} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <button onClick={() => setShowWalkIn(true)} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
          background: 'linear-gradient(135deg,#059669,#10b981)', color: 'white',
          border: 'none', borderRadius: '14px', padding: '13px',
          fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(5,150,105,0.28)',
        }}>
          <UserPlus size={16} /> Walk-in Sale
        </button>
        <button onClick={() => setShowExpense(true)} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
          background: 'white', color: '#dc2626',
          border: '1.5px solid #fecaca', borderRadius: '14px', padding: '13px',
          fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
        }}>
          <Receipt size={16} /> Add Expense
        </button>
      </div>

      {/* stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <StatCard
          label="Today"
          value={inr(today.revenue)}
          sub={`${today.bookings} completed`}
          icon={<Wallet size={16} />}
          accent="#dc2626"
          delay={0}
        />
        <StatCard
          label="This Week"
          value={inr(week.revenue)}
          sub={`${week.bookings} bookings`}
          icon={<TrendingUp size={16} />}
          accent="#7c3aed"
          delay={0.05}
        />
        <StatCard
          label="This Month"
          value={inr(month.revenue)}
          sub={growth !== null
            ? `${growth >= 0 ? '↑' : '↓'} ${Math.abs(growth)}% vs last month`
            : `${month.bookings} bookings`}
          icon={growth !== null && growth < 0 ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
          accent={growth !== null && growth < 0 ? '#f59e0b' : '#059669'}
          delay={0.1}
        />
        <StatCard
          label="Avg Ticket"
          value={inr(month.avgTicket)}
          sub="per customer"
          icon={<IndianRupee size={16} />}
          accent="#0891b2"
          delay={0.15}
        />
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'pending', label: `💰 To Settle${pendingSettlement.length ? ` (${pendingSettlement.length})` : ''}` },
          { id: 'products', label: `📦 Products${lowStock?.length ? ` (${lowStock.length}!)` : ''}` },
          { id: 'expenses', label: '🧾 Expenses' },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', borderRadius: '11px', border: 'none',
            cursor: 'pointer', whiteSpace: 'nowrap',
            fontWeight: 700, fontSize: '0.76rem',
            background: tab === t.id ? 'linear-gradient(135deg,#0f172a,#334155)' : 'white',
            color: tab === t.id ? 'white' : '#64748b',
            border: tab === t.id ? 'none' : '1px solid #e2e8f0',
            transition: 'all 0.2s ease',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gap: '14px' }}>
          <TrendChart data={dailyTrend} />

          {/* service vs product split */}
          {(month.productRevenue > 0 || month.serviceRevenue > 0) && (
            <div style={{
              background: 'white', borderRadius: '18px', padding: '18px',
              border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <p style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', fontFamily: "'Syne',sans-serif", marginBottom: '4px' }}>
                Services vs Products
              </p>
              <p style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '14px' }}>
                Where your money comes from · 30 days
              </p>

              <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', marginBottom: '14px', background: '#f1f5f9' }}>
                {month.serviceRevenue > 0 && (
                  <div style={{
                    width: `${(month.serviceRevenue / Math.max(month.revenue, 1)) * 100}%`,
                    background: 'linear-gradient(90deg,#7c3aed,#a78bfa)',
                  }} />
                )}
                {month.productRevenue > 0 && (
                  <div style={{
                    width: `${(month.productRevenue / Math.max(month.revenue, 1)) * 100}%`,
                    background: 'linear-gradient(90deg,#0891b2,#22d3ee)',
                  }} />
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ background: '#faf5ff', borderRadius: '12px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#7c3aed' }} />
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b21a8' }}>SERVICES</span>
                  </div>
                  <p style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                    {inr(month.serviceRevenue)}
                  </p>
                  <p style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{month.serviceCount} bookings</p>
                </div>

                <div style={{ background: '#ecfeff', borderRadius: '12px', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#0891b2' }} />
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0e7490' }}>PRODUCTS</span>
                  </div>
                  <p style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                    {inr(month.productRevenue)}
                  </p>
                  <p style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                    {month.productUnits} units
                    {month.productProfit > 0 && ` · ${inr(month.productProfit)} profit`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* low stock alert */}
          {lowStock?.length > 0 && (
            <div
              onClick={() => setTab('products')}
              style={{
                background: 'linear-gradient(135deg,#fffbeb,#fef3c7)',
                border: '1px solid #fcd34d', borderRadius: '16px',
                padding: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '11px',
              }}
            >
              <AlertTriangle size={19} color="#d97706" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e' }}>
                  {lowStock.length} product{lowStock.length > 1 ? 's' : ''} running low
                </p>
                <p style={{ fontSize: '0.7rem', color: '#b45309', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {lowStock.slice(0, 3).map((p) => `${p.name} (${p.stock})`).join(', ')}
                </p>
              </div>
              <span style={{ color: '#d97706', fontSize: '1.1rem' }}>›</span>
            </div>
          )}

          {/* top products */}
          {topProducts?.length > 0 && (
            <div style={{
              background: 'white', borderRadius: '18px', padding: '18px',
              border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <p style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', fontFamily: "'Syne',sans-serif", marginBottom: '14px' }}>
                Best Selling Products
              </p>
              {topProducts.map((p, i) => (
                <div key={p.name} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: i === topProducts.length - 1 ? 0 : '11px',
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: '0.79rem', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </p>
                    <p style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{p.units} units sold</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>{inr(p.revenue)}</p>
                    {p.profit > 0 && (
                      <p style={{ fontSize: '0.64rem', color: '#059669', fontWeight: 600 }}>
                        +{inr(p.profit)} profit
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* insights */}
          <div style={{
            background: 'linear-gradient(135deg,#0f172a,#1e293b)',
            borderRadius: '18px', padding: '18px',
          }}>
            <p style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' }}>
              Business Insights · 30 days
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
                <Clock size={15} color="#fbbf24" style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>
                    {insights.busiestHour || '—'}
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.62rem' }}>Busiest hour</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
                <Users size={15} color="#60a5fa" style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>
                    {insights.uniqueCustomers}
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.62rem' }}>Customers</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
                <Repeat size={15} color="#34d399" style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>
                    {insights.repeatRate}%
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.62rem' }}>Repeat rate</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
                <UserPlus size={15} color="#f472b6" style={{ flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>
                    {insights.walkIns}
                  </p>
                  <p style={{ color: '#64748b', fontSize: '0.62rem' }}>Walk-ins</p>
                </div>
              </div>
            </div>
          </div>

          {/* profit */}
          <div style={{
            background: 'white', borderRadius: '18px', padding: '18px',
            border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          }}>
            <p style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', fontFamily: "'Syne',sans-serif", marginBottom: '14px' }}>
              Profit · 30 days
            </p>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { label: 'Revenue', val: month.revenue, color: '#059669' },
                { label: 'Expenses', val: -expenseTotal, color: '#dc2626' },
              ].map((r) => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{r.label}</span>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: r.color }}>
                    {r.val < 0 ? '−' : ''}{inrFull(Math.abs(r.val))}
                  </span>
                </div>
              ))}
              <div style={{ height: '1px', background: '#f1f5f9', margin: '2px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>Net Profit</span>
                <span style={{
                  fontWeight: 800, fontSize: '1.15rem',
                  fontFamily: "'Syne',sans-serif",
                  color: netProfit >= 0 ? '#059669' : '#dc2626',
                }}>
                  {inrFull(netProfit)}
                </span>
              </div>
            </div>
          </div>

          {/* top services */}
          {topServices.length > 0 && (
            <div style={{
              background: 'white', borderRadius: '18px', padding: '18px',
              border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <p style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', fontFamily: "'Syne',sans-serif", marginBottom: '14px' }}>
                Top Services
              </p>
              {topServices.map((s, i) => (
                <div key={s.name} style={{ marginBottom: i === topServices.length - 1 ? 0 : '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '0.79rem', fontWeight: 600, color: '#334155' }}>
                      {s.name}
                    </span>
                    <span style={{ fontSize: '0.79rem', fontWeight: 800, color: '#0f172a' }}>
                      {inr(s.revenue)}
                      <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: '0.68rem' }}> · {s.count}x</span>
                    </span>
                  </div>
                  <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(s.revenue / maxSvc) * 100}%`, height: '100%',
                      background: 'linear-gradient(90deg,#7c3aed,#a78bfa)',
                      borderRadius: '3px',
                      animation: `growW 0.7s ease ${i * 0.08}s both`,
                    }} />
                  </div>
                </div>
              ))}
              <style>{`@keyframes growW { from { width: 0 } }`}</style>
            </div>
          )}

          {/* payment split */}
          {paySum > 1 && (
            <div style={{
              background: 'white', borderRadius: '18px', padding: '18px',
              border: '1px solid #f1f5f9', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <p style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', fontFamily: "'Syne',sans-serif", marginBottom: '14px' }}>
                How Customers Pay
              </p>
              <div style={{ display: 'flex', height: '9px', borderRadius: '5px', overflow: 'hidden', marginBottom: '12px' }}>
                {[
                  ['cash', '#059669'], ['upi', '#7c3aed'],
                  ['card', '#0891b2'], ['other', '#94a3b8'],
                ].map(([k, c]) => (
                  paymentSplit[k] > 0 && (
                    <div key={k} style={{ width: `${(paymentSplit[k] / paySum) * 100}%`, background: c }} />
                  )
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {[
                  ['cash', '#059669', '💵 Cash'], ['upi', '#7c3aed', '📱 UPI'],
                  ['card', '#0891b2', '💳 Card'], ['other', '#94a3b8', '📝 Other'],
                ].map(([k, c, label]) => (
                  paymentSplit[k] > 0 && (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: c, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{label}</span>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginLeft: 'auto' }}>
                        {inr(paymentSplit[k])}
                      </span>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PENDING SETTLEMENT ── */}
      {tab === 'pending' && (
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{
            background: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: '14px', padding: '12px 14px',
          }}>
            <p style={{ fontSize: '0.76rem', color: '#92400e', lineHeight: 1.6 }}>
              💡 Enter the <b>actual amount charged</b> after each service.
              This is essential for range-priced services — otherwise only the
              minimum price gets counted in your revenue.
            </p>
          </div>

          {pendingSettlement.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 20px', background: 'white',
              borderRadius: '18px', border: '1px solid #f1f5f9',
            }}>
              <p style={{ fontSize: '30px', marginBottom: '8px' }}>✅</p>
              <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>All settled</p>
              <p style={{ color: '#94a3b8', fontSize: '0.76rem', marginTop: '4px' }}>
                No confirmed bookings waiting for an amount
              </p>
            </div>
          ) : pendingSettlement.map((b) => (
            <div key={b.booking_id} style={{
              background: 'white', borderRadius: '16px', padding: '14px',
              border: '1px solid #f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.customer_name}
                </p>
                <p style={{ fontSize: '0.73rem', color: '#64748b', marginTop: '2px' }}>
                  {b.service_name}
                </p>
                <p style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '3px' }}>
                  {b.booking_date}{b.slot_time ? ` · ${b.slot_time}` : ''} · quoted {inrFull(b.quoted_price)}
                </p>
              </div>
              <button onClick={() => openSettle(b)} style={{
                background: 'linear-gradient(135deg,#dc2626,#e11d48)', color: 'white',
                border: 'none', borderRadius: '11px', padding: '9px 15px',
                fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', flexShrink: 0,
              }}>
                Enter ₹
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── PRODUCTS ── */}
      {tab === 'products' && (
        <div style={{ display: 'grid', gap: '10px' }}>
          <button
            onClick={() => {
              setProductForm({ product_id: null, name: '', price: '', cost_price: '', stock: '', low_stock_alert: '3', unit: 'pcs' });
              setShowProductForm(true);
            }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              background: 'linear-gradient(135deg,#0891b2,#22d3ee)', color: 'white',
              border: 'none', borderRadius: '14px', padding: '13px',
              fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(8,145,178,0.28)',
            }}
          >
            <Plus size={17} /> Add Product
          </button>

          {!productsLoaded ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '0.82rem' }}>
              Loading products…
            </div>
          ) : products.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 20px', background: 'white',
              borderRadius: '18px', border: '1px solid #f1f5f9',
            }}>
              <p style={{ fontSize: '30px', marginBottom: '8px' }}>📦</p>
              <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>No products yet</p>
              <p style={{ color: '#94a3b8', fontSize: '0.76rem', marginTop: '4px', lineHeight: 1.6 }}>
                Add shampoo, oil, wax or anything you sell.<br/>
                Track stock and see product profit separately.
              </p>
            </div>
          ) : products.map((p) => {
            const isLow = Number(p.stock) <= Number(p.low_stock_alert || 3);
            const outOfStock = Number(p.stock) <= 0;
            return (
              <div key={p.product_id} style={{
                background: 'white', borderRadius: '16px', padding: '14px',
                border: isLow ? '1.5px solid #fcd34d' : '1px solid #f1f5f9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <p style={{ fontWeight: 700, fontSize: '0.87rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </p>
                      {isLow && (
                        <span style={{
                          background: outOfStock ? '#fee2e2' : '#fef3c7',
                          color: outOfStock ? '#dc2626' : '#b45309',
                          fontSize: '0.6rem', fontWeight: 700,
                          padding: '2px 7px', borderRadius: '20px', flexShrink: 0,
                        }}>
                          {outOfStock ? 'OUT' : 'LOW'}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: '0.73rem', color: '#64748b', marginTop: '3px' }}>
                      {inrFull(p.price)}
                      {p.cost_price > 0 && (
                        <span style={{ color: '#059669' }}>
                          {' '}· {inr(p.price - p.cost_price)} margin
                        </span>
                      )}
                    </p>
                    <p style={{
                      fontSize: '0.71rem', marginTop: '2px',
                      color: outOfStock ? '#dc2626' : isLow ? '#d97706' : '#94a3b8',
                      fontWeight: isLow ? 700 : 400,
                    }}>
                      {p.stock} {p.unit || 'pcs'} in stock
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                      onClick={() => {
                        setProductForm({
                          product_id: p.product_id, name: p.name,
                          price: String(p.price), cost_price: String(p.cost_price || ''),
                          stock: String(p.stock), low_stock_alert: String(p.low_stock_alert || 3),
                          unit: p.unit || 'pcs',
                        });
                        setShowProductForm(true);
                      }}
                      style={{
                        width: '32px', height: '32px', borderRadius: '9px',
                        border: '1px solid #e2e8f0', background: 'white',
                        cursor: 'pointer', color: '#64748b',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      aria-label="Edit product"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => deleteProduct(p.product_id)}
                      style={{
                        width: '32px', height: '32px', borderRadius: '9px',
                        border: '1px solid #fecaca', background: 'white',
                        cursor: 'pointer', color: '#dc2626',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      aria-label="Delete product"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => { setSellFor(p); setSellQty('1'); setSellMode('cash'); }}
                  disabled={outOfStock}
                  style={{
                    width: '100%', marginTop: '11px', padding: '10px',
                    borderRadius: '11px', border: 'none',
                    background: outOfStock ? '#f1f5f9' : 'linear-gradient(135deg,#0891b2,#22d3ee)',
                    color: outOfStock ? '#94a3b8' : 'white',
                    fontWeight: 700, fontSize: '0.79rem',
                    cursor: outOfStock ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                >
                  <ShoppingCart size={14} />
                  {outOfStock ? 'Out of stock' : 'Sell'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── EXPENSES ── */}
      {tab === 'expenses' && (
        <div style={{ display: 'grid', gap: '10px' }}>
          <div style={{
            background: 'linear-gradient(135deg,#dc2626,#e11d48)',
            borderRadius: '16px', padding: '16px', color: 'white',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <p style={{ fontSize: '0.68rem', opacity: 0.85, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total · 30 days
              </p>
              <p style={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: "'Syne',sans-serif", marginTop: '3px' }}>
                {inrFull(expenseTotal)}
              </p>
            </div>
            <button onClick={() => setShowExpense(true)} style={{
              background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '11px', padding: '9px 14px', color: 'white',
              fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '5px',
            }}>
              <Plus size={14} /> Add
            </button>
          </div>

          {expenses.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 20px', background: 'white',
              borderRadius: '18px', border: '1px solid #f1f5f9',
            }}>
              <p style={{ fontSize: '30px', marginBottom: '8px' }}>🧾</p>
              <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem' }}>No expenses yet</p>
              <p style={{ color: '#94a3b8', fontSize: '0.76rem', marginTop: '4px' }}>
                Track rent, supplies and salaries to see real profit
              </p>
            </div>
          ) : expenses.map((e) => (
            <div key={e.expense_id} style={{
              background: 'white', borderRadius: '14px', padding: '13px',
              border: '1px solid #f1f5f9',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: '0.83rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.title}
                </p>
                <p style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '2px' }}>
                  {EXPENSE_CATEGORIES.find((c) => c.id === e.category)?.label || e.category} · {e.date}
                </p>
              </div>
              <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#dc2626', flexShrink: 0 }}>
                {inrFull(e.amount)}
              </span>
              <button onClick={() => deleteExpense(e.expense_id)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#cbd5e1', padding: '4px', flexShrink: 0,
              }} aria-label="Delete expense">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ══ SETTLE DIALOG ══ */}
      {settleFor && (
        <div onClick={() => !saving && setSettleFor(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'center', backdropFilter: 'blur(3px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '22px 22px 0 0',
            padding: '22px 20px 28px', width: '100%', maxWidth: '460px',
            animation: 'slideUp 0.28s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <p style={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                  Amount Charged
                </p>
                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                  {settleFor.customer_name} · {settleFor.service_name}
                </p>
              </div>
              <button onClick={() => setSettleFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={19} />
              </button>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              border: '2px solid #e2e8f0', borderRadius: '14px',
              padding: '13px 15px', marginBottom: '14px',
            }}>
              <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#94a3b8' }}>₹</span>
              <input
                type="number"
                inputMode="numeric"
                value={settleAmount}
                onChange={(e) => setSettleAmount(e.target.value)}
                autoFocus
                style={{
                  border: 'none', outline: 'none', flex: 1,
                  fontSize: '1.5rem', fontWeight: 800, color: '#0f172a',
                  fontFamily: "'Syne',sans-serif", minWidth: 0,
                }}
              />
            </div>

            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
              Payment mode
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
              {PAYMENT_MODES.map((m) => (
                <button key={m.id} onClick={() => setSettleMode(m.id)} style={{
                  padding: '11px', borderRadius: '12px', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.78rem',
                  border: settleMode === m.id ? '2px solid #dc2626' : '1.5px solid #e2e8f0',
                  background: settleMode === m.id ? '#fef2f2' : 'white',
                  color: settleMode === m.id ? '#dc2626' : '#64748b',
                }}>
                  {m.label}
                </button>
              ))}
            </div>

            <button onClick={submitSettle} disabled={saving} style={{
              width: '100%', padding: '15px', borderRadius: '15px', border: 'none',
              background: saving ? '#e2e8f0' : 'linear-gradient(135deg,#dc2626,#e11d48)',
              color: saving ? '#94a3b8' : 'white',
              fontWeight: 800, fontSize: '0.92rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
            }}>
              {saving ? 'Saving…' : <><Check size={17} /> Save</>}
            </button>
          </div>
          <style>{`@keyframes slideUp { from { transform: translateY(100%) } }`}</style>
        </div>
      )}

      {/* ══ WALK-IN DIALOG ══ */}
      {showWalkIn && (
        <div onClick={() => !saving && setShowWalkIn(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'center', backdropFilter: 'blur(3px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '22px 22px 0 0',
            padding: '22px 20px 28px', width: '100%', maxWidth: '460px',
            animation: 'slideUp 0.28s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
              <div>
                <p style={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                  Walk-in Sale
                </p>
                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                  Customer who didn't book online
                </p>
              </div>
              <button onClick={() => setShowWalkIn(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={19} />
              </button>
            </div>

            <input
              placeholder="Service (e.g. Haircut)"
              value={walkIn.service_name}
              onChange={(e) => setWalkIn({ ...walkIn, service_name: e.target.value })}
              style={{
                width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '12px',
                padding: '12px 14px', fontSize: '0.86rem', marginBottom: '10px', outline: 'none',
              }}
            />
            <input
              placeholder="Customer name (optional)"
              value={walkIn.customer_name}
              onChange={(e) => setWalkIn({ ...walkIn, customer_name: e.target.value })}
              style={{
                width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '12px',
                padding: '12px 14px', fontSize: '0.86rem', marginBottom: '10px', outline: 'none',
              }}
            />
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              border: '2px solid #e2e8f0', borderRadius: '12px',
              padding: '11px 14px', marginBottom: '14px',
            }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#94a3b8' }}>₹</span>
              <input
                type="number"
                inputMode="numeric"
                placeholder="Amount"
                value={walkIn.amount}
                onChange={(e) => setWalkIn({ ...walkIn, amount: e.target.value })}
                style={{
                  border: 'none', outline: 'none', flex: 1,
                  fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', minWidth: 0,
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
              {PAYMENT_MODES.map((m) => (
                <button key={m.id} onClick={() => setWalkIn({ ...walkIn, payment_mode: m.id })} style={{
                  padding: '10px', borderRadius: '11px', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.76rem',
                  border: walkIn.payment_mode === m.id ? '2px solid #059669' : '1.5px solid #e2e8f0',
                  background: walkIn.payment_mode === m.id ? '#f0fdf4' : 'white',
                  color: walkIn.payment_mode === m.id ? '#059669' : '#64748b',
                }}>
                  {m.label}
                </button>
              ))}
            </div>

            <button onClick={submitWalkIn} disabled={saving} style={{
              width: '100%', padding: '15px', borderRadius: '15px', border: 'none',
              background: saving ? '#e2e8f0' : 'linear-gradient(135deg,#059669,#10b981)',
              color: saving ? '#94a3b8' : 'white',
              fontWeight: 800, fontSize: '0.92rem',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              {saving ? 'Saving…' : 'Record Sale'}
            </button>
          </div>
        </div>
      )}

      {/* ══ EXPENSE DIALOG ══ */}
      {showExpense && (
        <div onClick={() => !saving && setShowExpense(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'center', backdropFilter: 'blur(3px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '22px 22px 0 0',
            padding: '22px 20px 28px', width: '100%', maxWidth: '460px',
            animation: 'slideUp 0.28s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
              <p style={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                Add Expense
              </p>
              <button onClick={() => setShowExpense(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={19} />
              </button>
            </div>

            <input
              placeholder="What was it for?"
              value={expense.title}
              onChange={(e) => setExpense({ ...expense, title: e.target.value })}
              style={{
                width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '12px',
                padding: '12px 14px', fontSize: '0.86rem', marginBottom: '10px', outline: 'none',
              }}
            />
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              border: '2px solid #e2e8f0', borderRadius: '12px',
              padding: '11px 14px', marginBottom: '14px',
            }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#94a3b8' }}>₹</span>
              <input
                type="number"
                inputMode="numeric"
                placeholder="Amount"
                value={expense.amount}
                onChange={(e) => setExpense({ ...expense, amount: e.target.value })}
                style={{
                  border: 'none', outline: 'none', flex: 1,
                  fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', minWidth: 0,
                }}
              />
            </div>

            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
              Category
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px', marginBottom: '18px' }}>
              {EXPENSE_CATEGORIES.map((c) => (
                <button key={c.id} onClick={() => setExpense({ ...expense, category: c.id })} style={{
                  padding: '10px', borderRadius: '11px', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.73rem',
                  border: expense.category === c.id ? '2px solid #dc2626' : '1.5px solid #e2e8f0',
                  background: expense.category === c.id ? '#fef2f2' : 'white',
                  color: expense.category === c.id ? '#dc2626' : '#64748b',
                }}>
                  {c.label}
                </button>
              ))}
            </div>

            <button onClick={submitExpense} disabled={saving} style={{
              width: '100%', padding: '15px', borderRadius: '15px', border: 'none',
              background: saving ? '#e2e8f0' : 'linear-gradient(135deg,#dc2626,#e11d48)',
              color: saving ? '#94a3b8' : 'white',
              fontWeight: 800, fontSize: '0.92rem',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              {saving ? 'Saving…' : 'Add Expense'}
            </button>
          </div>
        </div>
      )}

      {/* ══ PRODUCT FORM DIALOG ══ */}
      {showProductForm && (
        <div onClick={() => !saving && setShowProductForm(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'center', backdropFilter: 'blur(3px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '22px 22px 0 0',
            padding: '22px 20px 28px', width: '100%', maxWidth: '460px',
            maxHeight: '88vh', overflowY: 'auto',
            animation: 'slideUp 0.28s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
              <div>
                <p style={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                  {productForm.product_id ? 'Edit Product' : 'Add Product'}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                  Shampoo, oil, wax — anything you sell
                </p>
              </div>
              <button onClick={() => setShowProductForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={19} />
              </button>
            </div>

            <input
              placeholder="Product name"
              value={productForm.name}
              onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
              style={{
                width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '12px',
                padding: '12px 14px', fontSize: '0.86rem', marginBottom: '10px', outline: 'none',
              }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: '5px' }}>
                  Selling price *
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', border: '1.5px solid #e2e8f0', borderRadius: '11px', padding: '10px 12px' }}>
                  <span style={{ color: '#94a3b8', fontWeight: 700 }}>₹</span>
                  <input
                    type="number" inputMode="numeric" placeholder="300"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.95rem', fontWeight: 700, minWidth: 0 }}
                  />
                </div>
              </div>
              <div>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: '5px' }}>
                  Your cost
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', border: '1.5px solid #e2e8f0', borderRadius: '11px', padding: '10px 12px' }}>
                  <span style={{ color: '#94a3b8', fontWeight: 700 }}>₹</span>
                  <input
                    type="number" inputMode="numeric" placeholder="180"
                    value={productForm.cost_price}
                    onChange={(e) => setProductForm({ ...productForm, cost_price: e.target.value })}
                    style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.95rem', fontWeight: 700, minWidth: 0 }}
                  />
                </div>
              </div>
            </div>

            {Number(productForm.price) > 0 && Number(productForm.cost_price) > 0 && (
              <div style={{
                background: '#f0fdf4', borderRadius: '10px', padding: '9px 12px',
                marginBottom: '10px', fontSize: '0.74rem', color: '#166534',
              }}>
                💰 Profit per unit: <b>{inrFull(Number(productForm.price) - Number(productForm.cost_price))}</b>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '18px' }}>
              <div>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: '5px' }}>Stock</p>
                <input
                  type="number" inputMode="numeric" placeholder="0"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '11px', padding: '10px', fontSize: '0.88rem', fontWeight: 700, outline: 'none' }}
                />
              </div>
              <div>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: '5px' }}>Unit</p>
                <input
                  placeholder="pcs"
                  value={productForm.unit}
                  onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '11px', padding: '10px', fontSize: '0.88rem', outline: 'none' }}
                />
              </div>
              <div>
                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginBottom: '5px' }}>Alert at</p>
                <input
                  type="number" inputMode="numeric" placeholder="3"
                  value={productForm.low_stock_alert}
                  onChange={(e) => setProductForm({ ...productForm, low_stock_alert: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: '11px', padding: '10px', fontSize: '0.88rem', fontWeight: 700, outline: 'none' }}
                />
              </div>
            </div>

            <button onClick={submitProduct} disabled={saving} style={{
              width: '100%', padding: '15px', borderRadius: '15px', border: 'none',
              background: saving ? '#e2e8f0' : 'linear-gradient(135deg,#0891b2,#22d3ee)',
              color: saving ? '#94a3b8' : 'white',
              fontWeight: 800, fontSize: '0.92rem',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}>
              {saving ? 'Saving…' : productForm.product_id ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </div>
      )}

      {/* ══ SELL PRODUCT DIALOG ══ */}
      {sellFor && (
        <div onClick={() => !saving && setSellFor(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-end',
          justifyContent: 'center', backdropFilter: 'blur(3px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: '22px 22px 0 0',
            padding: '22px 20px 28px', width: '100%', maxWidth: '460px',
            animation: 'slideUp 0.28s cubic-bezier(0.16,1,0.3,1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '18px' }}>
              <div>
                <p style={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                  Sell {sellFor.name}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                  {inrFull(sellFor.price)} each · {sellFor.stock} {sellFor.unit || 'pcs'} available
                </p>
              </div>
              <button onClick={() => setSellFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={19} />
              </button>
            </div>

            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
              Quantity
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <button
                onClick={() => setSellQty(String(Math.max(1, Number(sellQty) - 1)))}
                style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  border: '1.5px solid #e2e8f0', background: 'white',
                  fontSize: '1.3rem', fontWeight: 700, color: '#64748b', cursor: 'pointer',
                }}
              >−</button>
              <input
                type="number" inputMode="numeric"
                value={sellQty}
                onChange={(e) => setSellQty(e.target.value)}
                style={{
                  flex: 1, textAlign: 'center', border: '2px solid #e2e8f0',
                  borderRadius: '12px', padding: '11px', fontSize: '1.3rem',
                  fontWeight: 800, color: '#0f172a', outline: 'none', minWidth: 0,
                  fontFamily: "'Syne',sans-serif",
                }}
              />
              <button
                onClick={() => setSellQty(String(Math.min(Number(sellFor.stock) || 1, Number(sellQty) + 1)))}
                style={{
                  width: '44px', height: '44px', borderRadius: '12px',
                  border: '1.5px solid #e2e8f0', background: 'white',
                  fontSize: '1.3rem', fontWeight: 700, color: '#64748b', cursor: 'pointer',
                }}
              >+</button>
            </div>

            <div style={{
              background: '#ecfeff', borderRadius: '12px', padding: '13px',
              marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: '0.82rem', color: '#0e7490', fontWeight: 600 }}>Total</span>
              <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', fontFamily: "'Syne',sans-serif" }}>
                {inrFull((Number(sellFor.price) || 0) * (Number(sellQty) || 0))}
              </span>
            </div>

            <p style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: '8px' }}>
              Payment mode
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
              {PAYMENT_MODES.map((m) => (
                <button key={m.id} onClick={() => setSellMode(m.id)} style={{
                  padding: '10px', borderRadius: '11px', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.76rem',
                  border: sellMode === m.id ? '2px solid #0891b2' : '1.5px solid #e2e8f0',
                  background: sellMode === m.id ? '#ecfeff' : 'white',
                  color: sellMode === m.id ? '#0891b2' : '#64748b',
                }}>
                  {m.label}
                </button>
              ))}
            </div>

            <button onClick={submitSale} disabled={saving} style={{
              width: '100%', padding: '15px', borderRadius: '15px', border: 'none',
              background: saving ? '#e2e8f0' : 'linear-gradient(135deg,#0891b2,#22d3ee)',
              color: saving ? '#94a3b8' : 'white',
              fontWeight: 800, fontSize: '0.92rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
            }}>
              {saving ? 'Saving…' : <><Check size={17} /> Record Sale</>}
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
